import { styled } from "@mui/material/styles";
import CreateIcon from "@mui/icons-material/Create";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ConfirmationModel from "components/ConfirmationModel";
import FileUploaderModel from "components/EditProgram/FileUploaderModel";
import Field from "components/Field";
import { compare, required } from "components/Field/validate";
import TitleHeader from "components/TitleHeader";
import { Formik } from "formik";
import { sha512 } from "js-sha512";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setHeaderDetails } from "store/actions/header";
import { uploadFile } from "store/actions/program";
import {
  changePassword,
  getProfile,
  getStripeBillingPortalLink,
  submitCancellationReason,
  updateNotificationPreference,
  updateProfile,
} from "store/actions/user";
import { formatDate } from "utils/common";

const CANCELLATION_REASONS = [
  { value: "too_expensive", label: "Trop cher" },
  { value: "no_longer_using", label: "Je n'utilise plus le service" },
  { value: "missing_features", label: "Fonctionnalités manquantes" },
  { value: "technical_issues", label: "Problèmes techniques" },
  { value: "other", label: "Autre" },
];

const StyleList = styled(List)(({ theme }) => ({
  ".MuiListItem-root": {
    padding: "24px 32px 32px 32px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },
  ".MuiListItemAvatar-root": {
    minWidth: "120px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "84px",
    },
  },
  ".MuiAvatar-root": {
    width: "96px",
    height: "96px",
    [theme.breakpoints.down("sm")]: {
      width: "72px",
      height: "72px",
    },
  },
}));

const Settings = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.userDetails);
  const userProfile = useSelector((state) => state.user.userProfile);
  const [showImageUploader, setImageUploader] = useState(false);
  const [userPicture, setUserPicture] = useState(userProfile?.picture || "");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDetails, setCancelDetails] = useState("");

  const isOwner = userDetails?.type === "OWNER";
  const isAdmin = userDetails?.type === "ADMIN";
  const subscription = userProfile?.organization?.subscription;
  const isFree = subscription?.type === "FREE" || !subscription?.type;
  const hasStripe = userProfile?.has_stripe === true;

  useEffect(() => {
    dispatch(getProfile());
    const navDetail = {
      isBreadcrumns: false,
      title: "Paramètres du compte",
    };
    dispatch(setHeaderDetails(navDetail));
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        dispatch(getProfile());
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  const toggleImageUploader = () => {
    setImageUploader(!showImageUploader);
  };
  const toggleCancelModal = () => {
    setShowCancelModal(!showCancelModal);
    if (showCancelModal) {
      setCancelReason("");
      setCancelDetails("");
    }
  };
  const handleCancelSubscription = () => {
    const reasonLabel =
      CANCELLATION_REASONS.find((r) => r.value === cancelReason)?.label ||
      cancelReason;
    dispatch(
      submitCancellationReason({
        reason: reasonLabel,
        details: cancelDetails,
      })
    ).then(() => {
      toggleCancelModal();
      dispatch(getStripeBillingPortalLink());
    });
  };
  return (
    <>
      <Container
        maxWidth="xxl"
        sx={{ backgroundColor: "#E4EAFC", padding: "0px !important" }}
      >
        <StyleList>
          <ListItem alignItems="flex-center">
            <ListItemAvatar>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <IconButton
                    onClick={toggleImageUploader}
                    sx={{
                      backgroundColor: "#0001FC",
                      height: "32px",
                      width: "32px",
                      borderRadius: "50%",
                      color: "#ffffff",
                      textAlign: "center",
                    }}
                  >
                    <CreateIcon fontSize="small" />
                  </IconButton>
                }
              >
                {userPicture ? (
                  <Avatar alt="user image" src={userPicture} />
                ) : (
                  <Avatar sx={{ bgcolor: "#0001FC", fontSize: "40px" }}>
                    {userProfile?.first_name ? userProfile?.first_name[0] : ""}
                  </Avatar>
                )}
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primaryTypographyProps={{ component: "div" }}
              secondaryTypographyProps={{ component: "div" }}
              primary={
                <>
                  <TitleHeader
                    title={`${userProfile?.first_name || ""} ${userProfile?.last_name || ""
                      }`}
                    fs={28}
                    fw={700}
                  ></TitleHeader>
                </>
              }
              secondary={
                <>
                  <Typography
                    component="div"
                    mb={0.5}
                    color="#111111"
                    variant="body2"
                  >
                    Membre depuis : {formatDate(userProfile?.created_on)}
                  </Typography>
                </>
              }
            />
          </ListItem>
        </StyleList>
      </Container>
      <Container
        maxWidth="xxl"
        sx={{ marginTop: { xs: 2, sm: 3 }, padding: { xs: "16px !important", sm: "32px !important" } }}
      >
        <Formik
          enableReinitialize={true}
          initialValues={{
            first_name: userProfile?.first_name || "",
            last_name: userProfile?.last_name || "",
            phone: userProfile?.phone || "",
            email: userProfile?.email || "",
          }}
          onSubmit={(values) => {
            const data = {
              ...values,
              role: userProfile?.role,
              _id: userProfile?._id,
            };
            dispatch(updateProfile(data));
          }}
        >
          {(props) => {
            const { handleSubmit } = props;
            return (
              <form onSubmit={handleSubmit}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={4}
                >
                  <TitleHeader title="Profil" fs={20} fw={600} />
                  <Button color="primary" type="submit" variant="contained">
                    Sauvegarder
                  </Button>
                </Stack>
                <Grid container spacing={3} mb={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      type="text"
                      label="Nom"
                      placeholder="Nom"
                      name="first_name"
                      validate={required}
                      mb={1}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      type="text"
                      label="Prénom"
                      placeholder="Prénom"
                      name="last_name"
                      validate={required}
                      mb={1}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      variant="outlined"
                      type="text"
                      label="Email"
                      placeholder="Email"
                      name="email"
                      validate={required}
                      mb={1}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      variant="outlined"
                      type="text"
                      label="Numéro"
                      placeholder="Numéro"
                      name="phone"
                      mb={1}
                    />
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
        <Divider sx={{ margin: { xs: "24px 0", sm: "40px 0 50px 0" } }} />
        <Formik
          enableReinitialize={true}
          initialValues={{
            current_password: "",
            new_password: "",
            confirm_password: "",
          }}
          onSubmit={(values, { resetForm }) => {
            const data = {
              current_password: sha512(values.current_password),
              new_password: sha512(values.new_password),
              confirm_password: sha512(values.confirm_password),
            };
            dispatch(changePassword(data)).then(() => {
              resetForm();
            });
          }}
        >
          {(props) => {
            const { values, handleSubmit } = props;
            return (
              <form onSubmit={handleSubmit}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={4}
                >
                  <TitleHeader title="Mot de passe" fs={20} fw={600} />
                  <Button color="primary" type="submit" variant="contained">
                    Sauvegarder
                  </Button>
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      type="password"
                      label="Mot de passe actuel*"
                      placeholder="Mot de passe actuel"
                      name="current_password"
                      validate={required}
                      mb={1}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      type="password"
                      label="Nouveau mot de passe"
                      placeholder="Nouveau mot de passe"
                      name="new_password"
                      validate={required}
                      mb={1}
                    />
                  </Grid>
                  <Grid item xs={0} sm={6} sx={{ display: { xs: "none", sm: "block" } }} />
                  <Grid item xs={12} sm={6}>
                    <Field
                      variant="outlined"
                      type="password"
                      label="Confirmer le nouveau mot de passe"
                      placeholder="Confirmer le nouveau mot de passe"
                      name="confirm_password"
                      validate={compare(
                        values.new_password,
                        values.confirm_password
                      )}
                      mb={1}
                    />
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
        <Divider sx={{ margin: { xs: "24px 0", sm: "40px 0 50px 0" } }} />

        <Formik
          enableReinitialize={true}
          initialValues={{
            client_program_completion:
              userProfile?.notification_preferences
                ?.client_program_completion || false,
            client_session_completion:
              userProfile?.notification_preferences
                ?.client_session_completion || false,
            client_account_setup:
              userProfile?.notification_preferences?.client_account_setup ||
              false,
            client_account_deletion:
              userProfile?.notification_preferences?.client_account_deletion ||
              false,
          }}
          onSubmit={(values) => {
            dispatch(updateNotificationPreference(values));
          }}
        >
          {(props) => {
            const { handleSubmit } = props;
            return (
              <form onSubmit={handleSubmit}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={4}
                >
                  <TitleHeader title="Notifications" fs={20} fw={600} />
                  <Button color="primary" type="submit" variant="contained">
                    Sauvegarder
                  </Button>
                </Stack>
                <TitleHeader title="M’alerter quand" mb={3} fs={16} fw={600} />
                <Field
                  type="switch"
                  label="Un client supprime son compte"
                  name="client_account_deletion"
                />
                <Field
                  type="switch"
                  label="Un client accepte une invitation"
                  name="client_account_setup"
                />
                <Field
                  type="switch"
                  label="Un client complète un programme"
                  name="client_program_completion"
                />
                <Field
                  type="switch"
                  label="Un client complète une séance"
                  name="client_session_completion"
                />
              </form>
            );
          }}
        </Formik>
        {isOwner && subscription && (
          <>
            <Divider sx={{ margin: { xs: "24px 0", sm: "40px 0 50px 0" } }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={4}
            >
              <TitleHeader title="Abonnement" fs={20} fw={600} />
              {!isFree && hasStripe && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => dispatch(getStripeBillingPortalLink())}
                >
                  Gérer mon abonnement
                </Button>
              )}
            </Stack>
            <Box
              sx={{
                border: `1px solid ${isFree ? "#ccc" : "#E38C3C"}`,
                borderRadius: "24px",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                p={3}
                sx={{
                  backgroundColor: isFree ? "#F5F5F5" : "#FBEDE0",
                  borderRadius: "24px 24px 0 0",
                }}
                gap={1}
              >
                <TitleHeader
                  title={subscription.name || "Evo Free"}
                  fs={20}
                  fw={600}
                />
                {isFree && (
                  <Typography
                    sx={{
                      backgroundColor: "#E0E0E0",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.5,
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Plan gratuit
                  </Typography>
                )}
              </Stack>
              <Box p={3}>
                {isFree ? (
                  <Box>
                    <Typography variant="body2" mb={2}>
                      Vous êtes actuellement sur le plan gratuit. Mettez à jour
                      votre abonnement pour accéder à toutes les
                      fonctionnalités.
                    </Typography>
                    <Button
                      color="primary"
                      variant="contained"
                      href="https://evocoach.fr/checkout"
                      target="_blank"
                    >
                      Mettre à jour mon abonnement
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      mb={2}
                    >
                      <TitleHeader
                        title="Membre depuis"
                        fs={14}
                        fw={400}
                      />
                      <TitleHeader
                        title={formatDate(subscription.start_date)}
                        fs={14}
                        fw={700}
                      />
                    </Stack>
                    {subscription.end_date && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        mb={2}
                      >
                        <TitleHeader
                          title="Renouvellement automatique le"
                          fs={14}
                          fw={400}
                        />
                        <TitleHeader
                          title={formatDate(subscription.end_date)}
                          fs={14}
                          fw={700}
                        />
                      </Stack>
                    )}
                    {hasStripe ? (
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        mt={1}
                      >
                        <Link
                          underline="hover"
                          color="#F05063"
                          sx={{
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                          onClick={toggleCancelModal}
                        >
                          Annuler l&apos;abonnement
                        </Link>
                      </Stack>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, color: "#666" }}
                      >
                        Pour gérer ou annuler votre abonnement, veuillez nous
                        contacter à{" "}
                        <Link href="mailto:contact@evocoach.fr">
                          contact@evocoach.fr
                        </Link>
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
        <Divider sx={{ margin: { xs: "24px 0", sm: "40px 0 50px 0" } }} />
        <TitleHeader title="Suppression de compte" fs={20} mb={3} fw={600} />
        <Box sx={{ backgroundColor: "#FDE3E6", borderRadius: "16px" }} p={3}>
          <Typography variant="body2">
            Pour supprimer votre compte, veuillez nous contacter à{" "}
            <Link href="mailto:contact@evocoach.fr">contact@evocoach.fr</Link>.
          </Typography>
        </Box>
        <ConfirmationModel
          isOpen={showCancelModal}
          title="Annuler l'abonnement"
          maxWidth="sm"
          body={
            <Box>
              <Typography variant="body2" mb={2}>
                Nous sommes désolés de vous voir partir. Pourriez-vous nous
                indiquer la raison de votre annulation ?
              </Typography>
              <RadioGroup
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              >
                {CANCELLATION_REASONS.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    value={reason.value}
                    control={<Radio />}
                    label={reason.label}
                  />
                ))}
              </RadioGroup>
              {(cancelReason === "other" ||
                cancelReason === "missing_features" ||
                cancelReason === "technical_issues") && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Dites-nous en plus... (obligatoire)"
                  value={cancelDetails}
                  onChange={(e) => setCancelDetails(e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          }
          handleClose={toggleCancelModal}
          onConfirm={handleCancelSubscription}
          disableConfirmButton={
            !cancelReason ||
            ((cancelReason === "missing_features" ||
              cancelReason === "technical_issues" ||
              cancelReason === "other") &&
              !cancelDetails.trim())
          }
          confirm="Continuer vers Stripe"
          confirmButtonColor="primary"
          back="Retour"
        />
        <Formik enableReinitialize={true} initialValues={{ user_picture: "" }}>
          <FileUploaderModel
            isOpen={showImageUploader}
            toggle={toggleImageUploader}
            isVideo={false}
            name="user_picture"
            title="Ajouter un document"
            validationMsg="Uniquement les fichiers JPEG, PNG jusqu'à 30 Mo."
            allowFileTypes={["image/jpeg", "image/png"]}
            callback={(file) => {
              return dispatch(
                uploadFile(file, "/api/upload/coach/thumbs/picture/new_thumb")
              ).then((res) => {
                if (res) {
                  setUserPicture(res);
                  const data = {
                    ...userProfile,
                    picture: res,
                  };
                  dispatch(updateProfile(data, "photo_upload"));
                }
                toggleImageUploader();
              });
            }}
          />
        </Formik>
      </Container>
    </>
  );
};

export default Settings;
